import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ImageOff, Link, Play } from 'lucide-react';
import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { facebookEndsFullBleed, facebookLook, isFacebookPost } from '../../../../lib/facebook-post-preview';
import { isSafeUrl } from '../../../../lib/safe-url';
import { AVATAR_PALETTE } from '../../../../lib/avatar-color';
import {
  socialPostCaptionIsLong,
  socialPostCaptionSegments,
  socialPostHasLogo,
  socialPostHeader,
  socialPostLinkHost,
  socialPostLogoKey,
  socialPostMediaLayout,
  socialPostMediaName,
  socialPostScheduleLine,
  socialPostGridRendition,
  socialPostImageRendition,
  socialPostSingleFrame,
  SOCIAL_POST_RENDITIONS,
  type SocialPostRendition,
  socialPostVideoAspect,
} from '../../../../lib/social-post-preview-card';
import { Avatar, Badge, Card, SocialLogo, TextButton } from '../../ui';
import { FacebookPostPreview } from './FacebookPostPreview';
import styles from './SocialPostPreviewCard.module.css';

const GRID_CLASS: Record<number, string> = { 2: styles.two, 3: styles.three, 4: styles.four };

export function SocialPostPreviewCard({ card }: { card: SocialPostPreviewCardMetadata }) {
  const { i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const seenRef = useItemSeenBi('social_post_preview_card', {
    additionalFields: {
      network: card.network,
      media: String(card.mediaCount),
      scheduled: String(Boolean(card.scheduledAt || card.scheduledText)),
    },
  });
  const [portrait, setPortrait] = useState(false);
  const look = isFacebookPost(card) ? facebookLook(card) : undefined;
  const expires = card.expiresWithinDay && card.labels.expires ? card.labels.expires : undefined;
  const openMedia = (media: SocialPostPreviewMedia, index: number) => {
    logPageClickBi({ page_name: 'chat', button_name: 'social_post_preview_media', answer: media.kind });
    openDocViewer({
      file: { name: socialPostMediaName(media, index), isApp: false, content: media.url },
      kind: media.kind,
      pageName: 'document_viewer',
      besideChat: true,
    });
  };
  const logCaptionToggle = (expanded: boolean) =>
    logPageClickBi({ page_name: 'chat', button_name: expanded ? 'social_post_preview_see_more' : 'social_post_preview_see_less' });
  const logLink = () => logPageClickBi({ page_name: 'chat', button_name: 'social_post_preview_link' });
  const cardClass = [
    styles.card,
    look === 'feed' && styles.facebookFeed,
    look === 'feed' && portrait && styles.facebookPortrait,
    look === 'feed' && facebookEndsFullBleed(card) && styles.facebookFlush,
    look === 'reel' && styles.facebookReel,
    look === 'story' && styles.facebookStory,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={seenRef} className={styles.root} data-testid="chat-social-post-preview-card" data-network={card.network}>
      <Card component="article" variant="sunken" disablePadding className={cardClass} aria-label={card.networkLabel} data-facebook-look={look}>
        {look ? (
          <FacebookPostPreview card={card} onOpenMedia={openMedia} onToggleCaption={logCaptionToggle} onOpenLink={logLink} onPortrait={setPortrait} />
        ) : (
          <GenericPostBody card={card} onOpenMedia={openMedia} onToggleCaption={logCaptionToggle} onOpenLink={logLink} />
        )}
        {(look !== 'feed' || expires) && (
          <footer className={look === 'feed' ? `${styles.footer} ${styles.facebookFooter}` : styles.footer}>
            {look !== 'feed' && <p className={styles.when}>{socialPostScheduleLine(card, i18n.language)}</p>}
            {expires && <p className={styles.footnote}>{expires}</p>}
          </footer>
        )}
      </Card>
    </div>
  );
}

function GenericPostBody({
  card,
  onOpenMedia: openMedia,
  onToggleCaption,
  onOpenLink,
}: {
  card: SocialPostPreviewCardMetadata;
  onOpenMedia: (media: SocialPostPreviewMedia, index: number) => void;
  onToggleCaption: (expanded: boolean) => void;
  onOpenLink: () => void;
}) {
  const { t } = useTranslation();
  const header = socialPostHeader(card);
  const layout = socialPostMediaLayout(card);
  const host = card.link && isSafeUrl(card.link) ? socialPostLinkHost(card.link) : undefined;
  const tileLabel = (media: SocialPostPreviewMedia) => (media.kind === 'video' ? t('docViewer.audioPlay') : t('files.type.image'));

  return (
    <>
      <header className={styles.head}>
        <AccountMark network={card.network} pictureUrl={card.accountPictureUrl} />
        <div className={styles.who}>
          <span className={styles.title} dir="auto" data-testid="social-post-title">
            {header.title}
          </span>
          {header.meta && (
            <span className={styles.meta} data-testid="social-post-meta">
              {header.meta}
            </span>
          )}
        </div>
        <Badge variant="waiting-on-a-reply" size="tiny" label={card.labels.badge} className={styles.pill} />
      </header>
      {card.caption && <Caption text={card.caption} seeMore={card.labels.seeMore} seeLess={card.labels.seeLess} onToggle={onToggleCaption} />}
      {(layout.tiles.length > 0 || layout.showsNotShown) && (
        <div className={styles.mediaZone}>
          {layout.tiles.length === 1 && (
            <SingleTile
              key={layout.tiles[0].url}
              media={layout.tiles[0]}
              postType={card.postType}
              label={tileLabel(layout.tiles[0])}
              onOpen={() => openMedia(layout.tiles[0], 0)}
            />
          )}
          {layout.tiles.length > 1 && (
            <div
              className={`${styles.grid} ${GRID_CLASS[layout.tiles.length]}`}
              data-testid="social-post-media-grid"
              data-count={layout.tiles.length}
            >
              {layout.tiles.map((media, index) => (
                <MediaTile
                  key={`${media.url}-${index}`}
                  media={media}
                  label={tileLabel(media)}
                  rendition={socialPostGridRendition(layout.tiles.length, index)}
                  large={layout.tiles.length === 3 && index === 0}
                  more={index === layout.tiles.length - 1 ? layout.more : 0}
                  onOpen={() => openMedia(media, index)}
                />
              ))}
            </div>
          )}
          {layout.showsNotShown && card.labels.mediaNotShown && (
            <p className={styles.notShown} data-testid="social-post-not-shown">
              {card.labels.mediaNotShown}
            </p>
          )}
        </div>
      )}
      {host && (
        <a className={styles.link} href={card.link} target="_blank" rel="noopener noreferrer" dir="ltr" onClick={onOpenLink}>
          <Link size={16} aria-hidden="true" className={styles.linkIcon} />
          <span className={styles.host}>{host}</span>
        </a>
      )}
    </>
  );
}

function AccountMark({ network, pictureUrl }: { network: string; pictureUrl?: string }) {
  const [failed, setFailed] = useState(false);
  const logo = socialPostLogoKey(network);
  const known = socialPostHasLogo(network);
  if (pictureUrl && !failed) {
    return (
      <span className={styles.mark} data-testid="social-post-account-picture">
        <Avatar initials="" photoUrl={pictureUrl} onPhotoError={() => setFailed(true)} size={36} color={AVATAR_PALETTE[0]} />
        {known && (
          <span className={styles.brandBadge} data-testid="social-post-brand-badge">
            <SocialLogo network={logo} size={12} />
          </span>
        )}
      </span>
    );
  }
  return (
    <span className={`${styles.mark} ${styles.markTile}`} data-testid="social-post-network-mark" data-known={String(known)}>
      <SocialLogo network={logo} size={18} />
    </span>
  );
}

function SingleTile({
  media,
  postType,
  label,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  postType: SocialPostPreviewCardMetadata['postType'];
  label: string;
  onOpen: () => void;
}) {
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const frame = socialPostSingleFrame(media.kind === 'video' ? socialPostVideoAspect(postType) : aspect);
  return (
    <MediaTile
      media={media}
      label={label}
      rendition={SOCIAL_POST_RENDITIONS.single}
      large
      more={0}
      className={frame.portrait ? styles.portrait : styles.landscape}
      style={{ aspectRatio: String(frame.aspectRatio) } as CSSProperties}
      onMeasure={media.kind === 'image' ? setAspect : undefined}
      onOpen={onOpen}
    />
  );
}

function MediaTile({
  media,
  label,
  large,
  more,
  rendition,
  className,
  style,
  onMeasure,
  onFail,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition: SocialPostRendition;
  large: boolean;
  more: number;
  className?: string;
  style?: CSSProperties;
  onMeasure?: (aspect: number | undefined) => void;
  onFail?: () => void;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const fail = () => {
    setFailed(true);
    onMeasure?.(undefined);
    onFail?.();
  };
  const measure = (width: number, height: number) => onMeasure?.(width > 0 && height > 0 ? width / height : undefined);
  return (
    <button
      type="button"
      className={[styles.tile, large && styles.lead, className].filter(Boolean).join(' ')}
      style={style}
      aria-label={label}
      data-kind={media.kind}
      onClick={onOpen}
    >
      {failed ? (
        <span className={styles.failed} data-testid="social-post-media-placeholder">
          {more === 0 && <ImageOff size={20} aria-hidden="true" />}
        </span>
      ) : media.kind === 'video' ? (
        <video
          className={styles.media}
          src={media.url}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={(event) => measure(event.currentTarget.videoWidth, event.currentTarget.videoHeight)}
          onError={fail}
        />
      ) : (
        <img
          className={styles.media}
          src={socialPostImageRendition(media.url, rendition)}
          alt=""
          loading="lazy"
          onLoad={(event) => measure(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)}
          onError={fail}
        />
      )}
      {media.kind === 'video' && !failed && more === 0 && (
        <span className={`${styles.play} ${large ? styles.playLarge : styles.playSmall}`} data-testid="social-post-play">
          <Play size={large ? 18 : 13} fill="currentColor" strokeWidth={0} aria-hidden="true" />
        </span>
      )}
      {more > 0 && (
        <span className={styles.more} data-testid="social-post-more">
          +{more}
        </span>
      )}
    </button>
  );
}

function Caption({ text, seeMore, seeLess, onToggle }: { text: string; seeMore: string; seeLess: string; onToggle: (expanded: boolean) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(() => socialPostCaptionIsLong(text));
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || expanded || node.clientHeight === 0) {
      return;
    }
    setOverflowing(node.scrollHeight > node.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className={styles.captionZone}>
      <p ref={ref} className={expanded ? styles.caption : `${styles.caption} ${styles.clamped}`} dir="auto" data-testid="social-post-caption">
        {socialPostCaptionSegments(text).map((segment, index) =>
          segment.highlighted ? (
            <span key={index} className={styles.tag}>
              {segment.text}
            </span>
          ) : (
            segment.text
          ),
        )}
      </p>
      {(expanded || overflowing) && (
        <TextButton
          size="small"
          className={styles.toggle}
          aria-expanded={expanded}
          onClick={() => {
            setExpanded(!expanded);
            onToggle(!expanded);
          }}
        >
          {expanded ? seeLess : seeMore}
        </TextButton>
      )}
    </div>
  );
}
